// The URL a browser asks THIS service for a file's bytes, and the one place its
// shape is declared.
//
// It is read from both ends and they must not be allowed to drift: the app's
// details screen builds it, and `cdn/origin-request/code/src/handlers/file.js`
// takes it apart again at the edge. A shape written out twice is a shape that
// diverges the first time anyone adds a segment, and the failure would be a 400
// from an edge function nobody is looking at.
//
// WHY THE SITE AND THE KIND ARE IN IT, AND THE BUCKET IS NOT
//
// `site-files-table` partitions on `${orgId}/${siteId}/${kind}` and ranges on
// `fileId` (see ./fields), and the orgId comes from the signed-in viewer rather
// than from the URL. So those three segments plus the viewer ARE the lookup, and
// the lookup succeeding IS the authorization decision: a row that resolves under
// the viewer's own organization is by construction a row they may read.
//
// bucketws' `bucketId` is deliberately absent. It is on the row, the edge reads
// it from there, and taking it from the caller instead would let anyone address
// any bucket pagews' service key can reach — which is every bucket pagews owns,
// for every one of its customers (docs/pagews-files.md §2).
//
// THE LAST SEGMENT IS COSMETIC, AND THAT IS ITS JOB
//
// Nothing is built from it: the object at the far end is `file.<ext>` with the
// `ext` off the row. It is in the URL because it is what a browser saves the
// download as, and `bucketws-9f3c…-uuid` is not a filename anybody wants. It is
// still validated at the edge, because a segment that reaches a router is a
// segment worth constraining whether or not today's code reads it.

import { isKind, KINDS } from "./kind";
// The hostname in the public form below is a key in another table, and it is
// normalized by the module that owns that table's key rather than here.
import { normalizeHost } from "@common/schema/site-domain/host";

export const FILE_PREFIX = "/file/";

// A percent-encoded segment cannot carry a separator, which is what keeps a
// filename with a slash in it from inventing a path. Applied to every segment
// rather than only the name: the ids are uuids and slugs today, and encoding a
// string that needs no encoding costs nothing.
const seg = (value) => encodeURIComponent(String(value == null ? "" : value));

// The name is `name` sans extension (see ./fields), and `name` has no pattern
// and defaults to "" — so a row can legitimately hold nothing. "file" rather
// than an empty segment, which would produce `/content/.png` and a URL whose
// last segment a browser saves as a dotfile.
export const contentFileName = (name, ext) => {
  const base = typeof name === "string" && name.trim() !== "" ? name : "file";
  return ext ? `${base}.${ext}` : base;
};

export const siteFileContentPath = (siteId, kind, fileId, filename) =>
  `${FILE_PREFIX}${seg(siteId)}/${seg(kind)}/${seg(fileId)}/content/${seg(filename)}`;

// The whole URL for a row, which is the form every caller in the app wants.
export const siteFileContentUrl = ({ siteId, kind, fileId, name, ext }) =>
  siteFileContentPath(siteId, kind, fileId, contentFileName(name, ext));

// Anchored, and each segment is `[^/]+` rather than `.+`, for the reason
// bucketws' `extractFileKeys` records against the identical shape: a greedy
// `.+` folds extra segments into whichever group wins the backtrack, so
// `a/b/c/d/content/x` would parse with a siteId of `a/b`. None of the three can
// contain a slash — the ids are uuids and the kind is one of two literals.
//
// Nothing is decoded here on purpose. `%2F` in a segment stays `%2F`, matches no
// row, and is a 400; decoding it first would turn it back into the separator the
// regex above exists to exclude.
export const extractSiteFileKeys = (uri) => {
  const match = /^([^/]+)\/([^/]+)\/([^/]+)\/content\/(.+)$/.exec(uri);
  if (!match) {
    return null;
  }

  const [, siteId, kind, fileId, path] = match;

  // The kind is an enum and it is part of a partition key, so a value that is
  // not one of the two can only ever miss. Refused here rather than read as a
  // miss so the log line says which of the two happened.
  if (!isKind(kind)) {
    return null;
  }

  return { siteId, kind, fileId, path };
};

// THE ANONYMOUS FORM, WHICH IS A DIFFERENT URL FOR A DIFFERENT CALLER
//
// Everything above answers a signed-in editor on the app's own hostname. A
// visitor to a published page is neither: they have no session, no key, and no
// reason to have heard of this service. What they have instead is the hostname
// they typed, which is `active` in `site-domains-table` and therefore names a
// site — and that row is the same fact the public renderer already trusts to
// decide which site's pages to serve (api/code/src/render, `by-host`).
//
// So the authorization is the same in shape and different in where the tenant
// comes from:
//
//   authenticated   orgId from the token   + siteId and kind from the URL
//   public          orgId AND siteId from the domain row + kind from the URL
//
// which is why neither `orgId` nor `siteId` appears below. Both are read off
// the hostname, so a visitor cannot address a site the hostname does not serve,
// and the URL carries nothing that identifies a tenant beyond the domain it is
// already being served on.
//
// WHAT A PAGE ACTUALLY EMBEDS IS THE RELATIVE FORM
//
// `siteFilePublicContentPath` has no host in it on purpose. Markup that names a
// hostname breaks the day a second domain is attached to the site, breaks again
// when one is detached, and is copied between sites by people who will not
// notice either. The host is inserted by the tenant's own edge — the Cloudflare
// Worker in docs/attaching-a-domain.md — exactly as it is for a page route, so
// one string works on every domain the site has and keeps working when they
// change.
//
// `by-host` is a reserved segment rather than a guess about the shape of the
// one that follows, for the reason api/code/src/render/index.js gives against
// the identical decision: sniffing "does it contain a dot" puts the tenancy
// boundary on how an id happens to be formatted today, and a literal cannot
// drift. The two forms are told apart by arity as well as by the literal, so a
// site whose siteId is the string `by-host` still parses as itself.
export const PUBLIC_HOST_SEGMENT = "by-host";

export const siteFilePublicContentPath = (kind, fileId, filename) =>
  `${FILE_PREFIX}${seg(kind)}/${seg(fileId)}/content/${seg(filename)}`;

export const siteFilePublicContentUrl = ({ kind, fileId, name, ext }) =>
  siteFilePublicContentPath(kind, fileId, contentFileName(name, ext));

// What the tenant's edge sends, and what the edge function parses back.
//
// Anchored and `[^/]+` per segment for the reason `extractSiteFileKeys` records
// above, and nothing is decoded, for the same one. The literal is checked
// first: a URI that does not begin `by-host/` is not this form and is left for
// the authenticated parse to answer for.
export const extractPublicSiteFileKeys = (uri) => {
  const match = /^([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/content\/(.+)$/.exec(uri);
  if (!match) {
    return null;
  }

  const [, prefix, host, kind, fileId, path] = match;

  if (prefix !== PUBLIC_HOST_SEGMENT) {
    return null;
  }

  if (!isKind(kind)) {
    return null;
  }

  // Normalized HERE and not by the caller, because the host is about to become
  // a DynamoDB key and that table is keyed on the bare lowercased hostname. A
  // visitor typing `Example.com` reaches a Worker that passes on what it was
  // given, and two spellings of one name must not be two lookups — which is
  // the whole argument `normalizeHost` carries, applied on the way out as it is
  // on the way in.
  return { host: normalizeHost(host), kind, fileId, path };
};

// THE THIRD FORM, FOR THE TWO PLACES A COMPOSED PAGE HAS NO HOSTNAME
//
//   /file/by-site/<orgId>/<siteId>/<kind>/<fileId>/content/<name>.<ext>
//
// The by-host form above resolves the tenant from the hostname the visitor
// arrived on, which is exactly right for a published site and useless in the
// two places an AUTHOR looks at a composed page:
//
//   the editor's preview   composes on the app's hostname, inside a
//                          `sandbox="allow-scripts"` frame. That frame's origin
//                          is opaque, so its site-for-cookies is null and the
//                          `sameSite: "lax"` session cookie is not sent — the
//                          AUTHENTICATED form is shut to it too, signed in or
//                          not. It has neither credential and no hostname.
//
//   /site/<orgId>/<siteId>/<route>   the explicit render URL on the
//                          distribution's own name. There is no domain row for
//                          `*.cloudfront.net` and there must not be, so the
//                          by-host form cannot be built there at all.
//
// In both, every image on the page was a 400 and the screens around it looked
// perfect. That was P193.
//
// WHAT MAKES THIS ONE SAFE IS THE FLAG, AND NOTHING ELSE
//
// This is the first of the three forms whose URL NAMES THE TENANT. The
// authenticated form takes the organization from a session token; the by-host
// form takes the organization and the site from a verified domain row; this one
// takes both from the caller. So there is no second, independent constraint
// behind it: `visibility: public` is the entire authorization decision, and a
// caller who knows four ids can ask for any file on the platform.
//
// That is a deliberate posture rather than an oversight — a public file is one
// somebody chose to put on a website, and a site's PAGES are already world
// -readable on this hostname whether or not a domain is attached (P183). But it
// means the flag is load-bearing here in a way it is not anywhere else: weaken
// `isPublic` and this route is one guess away from every file the platform
// holds. The edge reads the SITE row as well, so a removed site stops serving —
// see the handler for why that guard is here and not merely nice to have.
export const PUBLIC_SITE_SEGMENT = "by-site";

export const siteFileBySiteContentPath = (orgId, siteId, kind, fileId, filename) =>
  `${FILE_PREFIX}${PUBLIC_SITE_SEGMENT}/${seg(orgId)}/${seg(siteId)}/${seg(kind)}/${seg(fileId)}/content/${seg(filename)}`;

export const siteFileBySiteContentUrl = ({ orgId, siteId, kind, fileId, name, ext }) =>
  siteFileBySiteContentPath(orgId, siteId, kind, fileId, contentFileName(name, ext));

// Five segments before `/content/`, where the by-host form has four and the
// authenticated form three — so the three are told apart by arity as well as by
// their reserved literals, and none of the three parsers can answer for another
// one's URL. The tests assert that in every direction.
export const extractBySiteFileKeys = (uri) => {
  const match = /^([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/content\/(.+)$/.exec(uri);
  if (!match) {
    return null;
  }

  const [, prefix, orgId, siteId, kind, fileId, path] = match;

  if (prefix !== PUBLIC_SITE_SEGMENT) {
    return null;
  }

  if (!isKind(kind)) {
    return null;
  }

  // Nothing is lowercased here, unlike the by-host form's hostname. These are
  // ids and they are case-sensitive everywhere else they appear — folding them
  // would be inventing a second spelling for a partition key.
  return { orgId, siteId, kind, fileId, path };
};

// WHAT A COMPOSED PAGE PUTS IN AN `src`, AND WHY IT IS NOT ONE STRING
//
// A file reference is STORED relative — `/file/<kind>/<fileId>/content/<name>`,
// which is what the picker writes into an image param, into a paragraph's
// delta and into `faviconUrl`. That is the right thing to store: it survives a
// domain being attached, detached or swapped, and it is the same value however
// many domains the site has.
//
// It is not, however, the right thing to SERVE in every context, because the
// three routes above resolve the tenant from three different places. The stored
// form is complete only where the tenant's own edge inserts the hostname. In
// the two places a composed page has no hostname — the editor's preview frame
// and `/site/<orgId>/<siteId>/<route>` — it has to name the site instead.
//
// So the composer takes a BASE and the stored value stays put. One value, three
// contexts, and the only thing that varies is what precedes the kind:
//
//   tenant hostname          /file/                 (identity; the Worker acts)
//   preview frame            /file/by-site/<o>/<s>/
//   /site/<orgId>/<siteId>/  /file/by-site/<o>/<s>/
//
// The authenticated `/file/<siteId>/…` form is NOT one of them and must never
// be: it is the app's own chrome — the picker, the widgets, the file's screen —
// and a composed document carrying it would 401 for every visitor on the other
// two branches. Keeping it out of here is what makes "choose the base per
// caller" unnecessary rather than merely careful.
export const siteFileBaseOf = (orgId, siteId) =>
  `${FILE_PREFIX}${PUBLIC_SITE_SEGMENT}/${seg(orgId)}/${seg(siteId)}/`;

// Anchored on the KIND, which is what makes this safe to run over assembled
// markup. `/file/` alone would also match the two rebased forms — a second pass
// would turn `/file/by-site/o/s/images/…` into `/file/by-site/o/s/by-site/…` —
// and the kind enum is exactly the set that follows `/file/` in a STORED value
// and in neither rebased one. So the pass is idempotent by construction rather
// than by a caller remembering to run it once.
//
// The leading boundary is the other half, and the class is exactly the five
// characters a URL can follow in markup: `src="`, `href='`, `url(`, the bare
// `src=` of an unquoted attribute — legal HTML and reachable because tenant
// markup is raw — and the `,` between two `srcset` candidates.
//
// THE COMMA IS THERE FOR `srcset`, AND ONLY BECAUSE THE CANDIDATES CARRY NO
// SPACE. A paragraph image now offers bucketws' derived widths
// (@common/schema/site-file/sizes), one stored path per candidate, and every
// candidate after the first is preceded by the separator rather than by a
// quote. The grammar allows `a 100w,b 200w`, the renderer writes it that way,
// and so the character before each of them is exactly one — which prose does
// not produce: a path written after a comma in a sentence has a space in
// between. Emit `, ` instead and the whitespace note below applies in full.
//
// WHITESPACE IS DELIBERATELY NOT IN IT. Adding it looks harmless — and it is
// what this was first written with, on the reasoning that an unquoted
// attribute needs it. That reasoning is wrong, and a test is what showed it:
// `<img src=/file/…>` puts an `=` before the path, never a space. So the only
// thing whitespace admits is a path in running PROSE, which then gets rewritten
// — a page documenting this feature would show its readers the by-site form.
// The class below covers every real position and leaves prose alone.
const STORED_PATH = new RegExp(
  `(^|["'(=,])${FILE_PREFIX}(${KINDS.join("|")})/`,
  "g"
);

const rebasing = (fileBase) =>
  typeof fileBase === "string" && fileBase !== "" && fileBase !== FILE_PREFIX;

// Every stored file path in a block of assembled markup, rebased.
//
// It runs over the ASSEMBLED, ALREADY-INTERPOLATED body for the reason
// `bodyOf`'s own comment gives for the interpolation pass it follows: that is
// the one place that reaches every element's output whatever route it took to
// get here. An image param arrives through a section template, a paragraph
// image never goes near a template at all, and a hand-written `<img>` in a
// `code` element goes through neither — three routes, one seam.
export const rebaseFilePaths = (markup, fileBase) => {
  if (!rebasing(fileBase) || typeof markup !== "string" || markup === "") {
    return markup;
  }

  return markup.replace(STORED_PATH, (_m, lead, kind) => `${lead}${fileBase}${kind}/`);
};

// One value rather than a block of markup — `faviconUrl`, which is interpolated
// into an attribute of its own and never passes through the body.
export const rebaseFileUrl = (url, fileBase) => {
  if (!rebasing(fileBase) || typeof url !== "string") {
    return url;
  }

  const rest = url.startsWith(FILE_PREFIX) ? url.slice(FILE_PREFIX.length) : null;

  if (rest === null || !isKind(rest.split("/")[0])) {
    return url;
  }

  return `${fileBase}${rest}`;
};
